import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Grid, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Box } from "@mui/system";
import ErrorBoundary from "../../../../common/ErrorBoundary";
import { TableSkeleton } from "../../../../common/Skeletons";
import { formatNumber } from "../../../../helpers/UtilityHelper";
import useRVP from "./useRVP";
import StatusWiseSplit from "./StatusWiseSplit";
import CourierWiseSplit from "./CourierWiseSplit";
import PickupAgeing from "./PickupAgeing";
import DeliveryAgeing from "./DeliveryAgeing";
import InfoBox from "../../../../common/InfoBox";
import CourierWiseAgeingSplit from "./CourierWiseAgeingSplit";
import CourierWiseDeliveryPerformance from "./CourierWiseDeliveryPerformance"
import PickupAttemptAgeing from "./PickupAttemptAgeing";
import PickupAttemptAgeingCourierWise from "./PickupAttemptAgeingCourierWise";
import DayWiseAttempt from "../../../../common/DayWiseAttempt";
import DeliveryTat from "./DeliveryTat"
import ZoneWiseSplit from "./ZoneWiseSplit"
import NPRRemarksAnalysis from "./NPRRemarksAnalysis"
import CancellationRemarksByAttempt from "./CancellationRemarksByAttempt"

function RVPComponent() {

    const [calcOn, setCalcOn] = React.useState('DOD')
    const { isLoading, data, allPickupShipments } = useRVP(calcOn)

    return (
        <Accordion expanded>
            <AccordionSummary sx={{ borderRadius: 1, boxShadow: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>RVP Summary</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ToggleButtonGroup
                                color="primary"
                                value={calcOn}
                                exclusive
                                size="small"
                                onChange={(_e, v) => setCalcOn(v)}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        border: '1px solid',
                                    }
                                }}
                            >
                                <ToggleButton value="DOD">DoD</ToggleButton>
                                <ToggleButton value="WOW">WoW</ToggleButton>
                                <ToggleButton value="MOM">MoM</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                        The data below provides an overall summary of all reverse shipments. All data is populated on basis of the date of shipment creation in Proship.
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: '#f5f8fa', px: 0 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        {isLoading && <TableSkeleton rows={10} />}
                        {!isLoading && (
                            <Grid container spacing={1}>
                                <Grid item xs={12 / 5}>
                                    <InfoBox header="Total RVP Orders" value={formatNumber(data?.totalRVPOrders)} />
                                </Grid>
                                <Grid item xs={12 / 5}>
                                    <InfoBox header="PickedUp" value={`${formatNumber(data?.pickedUpRVP)} (${data?.rvpPickupPercentage}%)`} />
                                </Grid>
                                <Grid item xs={12 / 5}>
                                    <InfoBox header="Pending Pickup" value={`${formatNumber(data?.pendingPickup)} (${data?.rvpPendingPickupPercentage}%)`} />
                                </Grid>
                                <Grid item xs={12 / 5}>
                                    <InfoBox header="Delivered" value={`${formatNumber(data?.delivered)} (${data?.rvpDeliveredPercentage}%)`} />
                                </Grid>
                                <Grid item xs={12 / 5}>
                                    <InfoBox header="In-Transit" value={formatNumber(data?.intransit)} />
                                </Grid>
                                {data && data?.totalRVPOrders > 0 && (
                                    <>
                                        <Grid item xs={12}>
                                            <StatusWiseSplit data={data?.chartData || []} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <CourierWiseSplit uniqCourierIds={data?.uniqCourierIds || []} data={data?.chartData || []} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <CourierWiseAgeingSplit
                                                title="Courier Wise Pickup Ageing"
                                                data={data?.courierWiseAgeingPickup || []}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <CourierWiseAgeingSplit
                                                title="Courier Wise Delivery Ageing"
                                                data={data?.courierWiseAgeingDelivery || []}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <CourierWiseDeliveryPerformance
                                                title='Courier Wise - Delivery Out-TAT Trend'
                                                data={data?.courierOrMerchantWiseDelivered || []}
                                                uniqCourierIds={data?.uniqCourierIds || []}
                                                uniqMerchantIds={data?.uniqMerchantIds || []}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PickupAttemptAgeing
                                                data={data?.chartData || []}
                                                title='Pickup Attempt Count Split'
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PickupAttemptAgeingCourierWise
                                                data={data?.courierWisePickupAttemptAgeing || []}
                                                title='Courier Wise Pickup Attempt Ageing'
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DayWiseAttempt
                                                key={calcOn}
                                                title='Day Wise Pickup Attempt'
                                                data={allPickupShipments}
                                                groupOn={calcOn}
                                                isRvp
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DeliveryTat data={data?.groupedByInOutTatData || []} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <ZoneWiseSplit data={data?.chartData || []} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <CourierWiseSplit
                                                uniqCourierIds={data?.uniqCourierIds || []}
                                                data={data?.chartData || []}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <PickupAgeing data={data?.chartData || []} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DeliveryAgeing data={data?.chartData || []} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <NPRRemarksAnalysis data={data?.nprRemarksData} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <CancellationRemarksByAttempt rawData={data?.rawData || []} />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
}

export default function RVP() {
    return (
        <ErrorBoundary>
            <RVPComponent />
        </ErrorBoundary>
    )
}